// store/VenueContext.tsx

import React, {
  createContext,
  useContext,
  ReactNode,
  useRef,
  useEffect,
} from "react";
import { create, useStore } from "zustand";
import { venueApi } from "@/services/api";
import moment from "moment";
import {
  ReverseBookingResponseType,
  VenueCheckoutConfig,
  VenueConfigType,
  VenueOffer,
  VenueType,
} from "@/services/types/venue.types";
import { persist } from "zustand/middleware";
import { delay } from "@/utils";

const initVenueConfig: VenueConfigType = {
  booking_date: moment(), // or moment().startOf('day') depending on your use case
  courts: 0,
  slot_time: "",
  venue_type: "",
  current_page: "1", // assuming default page is "1" as string
  selected_slots: [],
  selectedOfferId: null,
  price: null,
  discountedPrice: 0,
  timeSelected: null,
  timeslotRepresentations: null,
  offer: null,
  offerIndex: null,
  valid: null,
  validity: null,
  slots: null,
  switch_toggle: null,
  timelineSelected: null,
  limit: null,
  format: null,
  selectedBookingDateSlide: 1,
  selectedCourtsSlide: 1,
};
export const initVenueCheckoutConfig: VenueCheckoutConfig = {
  format: "",
  booking_amount: 0,
  payingUsingCoins: false,
  limit: 0,
  service_fee: 0,
  coins: 0,
  fullPayment: false,
  user: "",
  userId: "",
  amount: 0,
};
export interface VenueStore {
  hydrated: boolean;
  venueId: string | null;
  venueData: VenueType | null;
  venueSlots: any | null;
  venueOffers: VenueOffer[] | null;
  venueConfig: VenueConfigType | null;
  venueBlockSlotConfig: any | null;
  venueJoiningStatus: any | null;
  venueCheckoutConfig: VenueCheckoutConfig | null;
  setVenueConfig: (
    configOrUpdater:
      | VenueConfigType
      | ((prev: VenueConfigType) => VenueConfigType)
  ) => void;
  setVenueCheckoutConfig: (
    configOrUpdater:
      | VenueCheckoutConfig
      | ((prev: VenueCheckoutConfig) => VenueCheckoutConfig)
  ) => void;

  loadingVenueData: boolean;
  loadingVenueOffers: boolean;
  loadingVenueSlots: boolean;
  error: string | null;
  fetchVenue: (id: string, useCache?: boolean) => Promise<void>;
  fetchSlotsAndOffers: (
    venueId: string,
    userId: string,
    venueConfig: any,
    courts:number,
    useCache?: boolean,
  ) => Promise<void>;
  reverseVenueBooking: (
    bookingId: string
  ) => Promise<ReverseBookingResponseType | null | undefined>;
  setVenueBlockSlotConfig: (config: any) => void;
  setVenueJoiningStatus: (status: any) => void;
  resetVenueConfigPrice: () => void;
  resetVenueConfigSlots: () => void;
  resetVenueConfig: () => void;
  setHydrated: (val: boolean) => void;
}

// Create the Zustand store
const createVenueStore = () =>
  create<VenueStore>()(
    persist(
      (set) => ({
        hydrated: false,
        venueId: null,
        venueData: null,
        venueSlots: null,
        venueOffers: null,
        venueConfig: initVenueConfig,
        venueCheckoutConfig: initVenueCheckoutConfig,
        venueBlockSlotConfig: null,
        venueJoiningStatus: null,
        error: null,
        loadingVenueData: true,
        loadingVenueOffers: true,
        loadingVenueSlots: true,
        fetchVenue: async (id, useCache = true) => {
          set({ loadingVenueData: true, error: null });
          try {
            const response = await venueApi.getVenueDetails(id, false);
            set({
              venueData: response?.data ?? [],
              loadingVenueData: false,
              venueId: response?.data?._id,
            });
          } catch (error: any) {
            console.error("❌ Fetch Games Error:", error.message);
            set({ error: error.message, loadingVenueData: false });
          }
        },
        fetchSlotsAndOffers: async (venueId, userId, venueConfig,courts) => {
          
          try {
            let body = {
              venueId: venueId,
              userId: userId,
              date: moment(venueConfig.booking_date).set({
                hour: 0,
                minute: 0,
                second: 0,
                millisecond: 0,
              }),
              date1:
                moment(venueConfig.booking_date).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                }) ||
                moment().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }),
              base_type: venueConfig?.venue_type,
              courts: courts,
            };
            if (!venueConfig?.venue_type || venueConfig?.courts === undefined)
              return;
            set({
              loadingVenueOffers: true,
              loadingVenueSlots: true,
              error: null,
            });
            const slotOfferResponse = await venueApi.fetchSlotsAndOffer(body);
            set({
              venueSlots: slotOfferResponse.slotPicker,
              venueOffers: slotOfferResponse.offerFetched,
            });
            await delay(300);
            set({
              loadingVenueOffers: false,
              loadingVenueSlots: false,
            });
          } catch (error: any) {
            console.error("❌ Fetch Games Error:", error.message);
            set({
              error: error.message,
              loadingVenueOffers: false,
              loadingVenueSlots: false,
            });
          }
        },
        reverseVenueBooking: async (
          bookingId: string,
          removeVenueConfig?: any
        ) => {
          try {
            const response = await venueApi.reverseVenueBooking(bookingId);
            if (removeVenueConfig) {
              set({ venueConfig: null });
            }
            return response;
          } catch (e) {
            console.error("❌ reverseVenueBooking Error:", e);
          }
        },
        setVenueConfig: (configOrUpdater) => {
          set((state) => ({
            venueConfig:
              typeof configOrUpdater === "function"
                ? configOrUpdater(state.venueConfig!)
                : configOrUpdater,
          }));
        },
        setVenueCheckoutConfig: (configOrUpdater) => {
          set((state) => ({
            venueCheckoutConfig:
              typeof configOrUpdater === "function"
                ? configOrUpdater(state.venueCheckoutConfig!)
                : configOrUpdater,
          }));
        },
        setVenueBlockSlotConfig: (config) => {
          set({ venueBlockSlotConfig: config });
        },
        resetVenueConfigPrice: () => {
          set((state) => {
            if (!state.venueConfig) return {};
            return {
              venueConfig: {
                ...state.venueConfig,
                price: null,
                discountedPrice: null,
                timeSelected: null,
              },
            };
          });
        },
        resetVenueConfigSlots: () => {
          set((state) => {
            if (!state.venueConfig) return {};
            return {
              venueConfig: {
                ...initVenueConfig,
                booking_date: state.venueConfig.booking_date,
                venue_type: state.venueConfig.venue_type,
                courts: state.venueConfig.courts,
                limit: state.venueConfig.limit,
              },
            };
          });
        },
        resetVenueConfig: () => {
          set((state) => {
            return {
              venueSlots: null,
              venueOffers: null,
              venueConfig: initVenueConfig,
              venueCheckoutConfig: initVenueCheckoutConfig,
              venueBlockSlotConfig: null,
              venueJoiningStatus: null,
            };
          });
        },
        setVenueJoiningStatus: (status: string) => {
          set((state) => {
            return {
              venueJoiningStatus: status,
            };
          });
        },
        setHydrated: (val) => set({ hydrated: val }),
      }),
      {
        name: "venue-store", // localStorage key
        storage: {
          getItem: (name) => {
            const item = sessionStorage.getItem(name);
            return item ? JSON.parse(item) : null;
          },
          setItem: (name, value) => {
            sessionStorage.setItem(name, JSON.stringify(value));
          },
          removeItem: (name) => {
            sessionStorage.removeItem(name);
          },
        },
        onRehydrateStorage: (state) => {
          // rehydrates data on app remount or page reload
          return (storedState: any) => {
            state?.setHydrated(true);

            // if (storedState?.venueData?._id !== state.venueId) {
            //   state.setVenueBlockSlotConfig(null);
            //   state.setVenueCheckoutConfig(initVenueCheckoutConfig);
            //   state.setVenueJoiningStatus(null);
            //   state.resetVenueConfig();
            // }
            state.setVenueJoiningStatus(null);
            if (storedState?.venueConfig?.booking_date) {
              storedState.venueConfig.booking_date = moment(
                storedState.venueConfig.booking_date
              );
              if (storedState?.venueOffers) {
                storedState.venueOffers = storedState.venueOffers;
              }
            }
          };
        },
      }
    )
  );

// Create the context
const VenueStoreContext = createContext<ReturnType<
  typeof createVenueStore
> | null>(null);

// Provider component
export const VenueStoreProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<ReturnType<typeof createVenueStore>>();

  if (!storeRef.current) {
    storeRef.current = createVenueStore();
  }

  useEffect(() => {
    if (!storeRef.current) return;
    const unsubscribe = storeRef.current.subscribe((state) => {
      console.log("VenueStoreProvider State updated:", state);
    });

    return () => unsubscribe();
  }, [storeRef.current]);

  return (
    <VenueStoreContext.Provider value={storeRef.current}>
      {children}
    </VenueStoreContext.Provider>
  );
};

// Hook to use the store
export function useVenueStore<T>(selector: (state: VenueStore) => T): T {
  const context = useContext(VenueStoreContext);
  if (!context) {
    throw new Error("useVenueStore must be used within a VenueStoreProvider");
  }
  return useStore(context, selector);
}
